import styled from "styled-components";
import { Card, Col, Form, Row, Select } from "antd";
import moment from "moment";
import { useForm } from "antd/lib/form/Form";
import { forwardRef, useEffect, useState } from "react";
import { theme } from "../../../theme";
import { CustomForm } from "../../../components/organisms/Form/index.styled";
import { CustomSelect } from "../../../components/organisms/Select";
import { Caution, Question } from "../../../components/organisms/Icon";
import { CustomRangePicker } from "../../../components/organisms/DatePicker/Range";
import { FilterExtraBtnComp } from "../../../components/organisms/Filter/Extra";
import { SearchInput } from "../../../components/organisms/Input/Search";
import { IconForm } from "../../../components/organisms/Icon/index.styled";
import color from "../../../theme/color";
import {
  SettlementListSearchQuery,
  SettlementStatusTypes,
} from "../../../apis/Settlement/types";
import { translateSettlementStatusTypes } from "../../../shared/constants";

type PeriodTypes = "월간" | "직접선택";

interface FormProps {
  period: PeriodTypes;
  month: number;
  customerName?: string;
  createdAtRange?: string[];
  settlementStatus?: SettlementStatusTypes;
}

interface Props {
  onSearch: (props: SettlementListSearchQuery) => void;
}

export default forwardRef<HTMLDivElement, Props>(function SettlementFilterForm(
  { onSearch },
  ref
) {
  const [form] = useForm();

  const currentMonth = moment().get("M") + 1;

  const [period, setPeriod] = useState<PeriodTypes>("월간");
  const [month, setMonth] = useState(currentMonth);

  const isMonth = period === "월간";

  useEffect(() => {
    form.setFieldValue("month", month);
    form.setFieldValue("period", period);
  }, [month, period]);

  const handleFinish = (values: any) => {
    const props = { ...values } as FormProps;

    let startedAt = "";
    let endedAt = "";

    if (props.period === "월간" && month !== undefined && month !== null) {
      startedAt = moment()
        .month(month - 1)
        .startOf("month")
        .format("YYYY-MM-DD");
      endedAt = moment()
        .month(month - 1)
        .endOf("month")
        .format("YYYY-MM-DD");
    } else if (
      props.period === "직접선택" &&
      props.createdAtRange &&
      props.createdAtRange.length > 1
    ) {
      const [start, end] = props.createdAtRange;
      startedAt = moment(start).format("YYYY-MM-DD");
      endedAt = moment(end).format("YYYY-MM-DD");
    }

    onSearch({
      startedAt,
      endedAt,
      customerName: props.customerName,
      settlementStatus: props.settlementStatus,
    });
  };

  const handleReset = () => {
    form.resetFields();
    setPeriod("월간");
    setMonth(currentMonth);
    onSearch({
      startedAt: moment().startOf("month").format("YYYY-MM-DD"),
      endedAt: moment().endOf("month").format("YYYY-MM-DD"),
      customerName: undefined,
      settlementStatus: undefined,
    });
  };

  return (
    <Card
      ref={ref}
      bordered={false}
      headStyle={{ padding: "0px 16px" }}
      bodyStyle={{ padding: 16, marginBottom: 24 }}
      extra={
        <FilterExtraBtnComp onReload={handleReset} onSearch={form.submit} />
      }
    >
      <CustomForm
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
        labelAlign="left"
        colon={false}
        noValidate
        onFinish={handleFinish}
        validateMessages={{
          required: "필수 입력 값입니다.",
        }}
      >
        <Row gutter={[24, 12]} justify="space-between">
          <Col span={8}>
            <Form.Item name="period" label={<FilterLabel>기간</FilterLabel>}>
              <CustomSelect
                showArrow
                placeholder="기간을 선택해주세요."
                defaultValue="월간"
                onChange={(v) => setPeriod(v as PeriodTypes)}
              >
                {["월간"].map((v) => (
                  <Select.Option key={v} value={v} label={v}>
                    {v}
                  </Select.Option>
                ))}
              </CustomSelect>
            </Form.Item>
          </Col>
          <Col span={8}>
            {isMonth ? (
              <>
                <Form.Item
                  name="month"
                  label={
                    <FilterLabel>
                      장착일
                      <br />
                      (정산기준)
                    </FilterLabel>
                  }
                >
                  <CustomSelect
                    showArrow
                    placeholder="기간을 선택해주세요."
                    defaultValue={month}
                    onChange={(v) => setMonth(v as number)}
                  >
                    {Array.from({ length: 18 }, (_, i) => i - 5).map((v) => (
                      <Select.Option key={v} value={v} label={v}>
                        {moment().month(v).format("YY년 MM월")}(
                        {moment()
                          .month(v - 1)
                          .startOf("month")
                          .format("MM/DD")}{" "}
                        ~{" "}
                        {moment()
                          .month(v - 1)
                          .endOf("month")
                          .format("MM/DD")}
                        )
                      </Select.Option>
                    ))}
                  </CustomSelect>
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  name="createdAtRange"
                  rules={[{ required: true }]}
                  label={
                    <FilterLabel>
                      장착일
                      <br />
                      (정산기준)
                    </FilterLabel>
                  }
                >
                  <CustomRangePicker
                    size="small"
                    placeholder={["시작일", "종료일"]}
                    format="YYYY-MM-DD"
                    allowClear
                    onChange={(values) => {
                      if (values && values.length === 2) {
                        form.setFieldsValue({
                          createdAtRange: [
                            moment(values[0]).startOf("day"),
                            moment(values[1]).endOf("day"),
                          ],
                        });
                      }
                    }}
                  />
                </Form.Item>
                <Form.Item noStyle shouldUpdate>
                  <Form.Item noStyle name="createdAtFrom" />
                  <Form.Item noStyle name="createdAtTo" />
                </Form.Item>
              </>
            )}
          </Col>
          <Col span={8}>
            <Form.Item
              name="customerName"
              label={<FilterLabel>고객명</FilterLabel>}
            >
              <SearchInput allowClear placeholder="고객명을 입력해주세요." />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="settlementStatus"
              label={<FilterLabel>정산상태</FilterLabel>}
            >
              <CustomSelect allowClear showArrow placeholder="선택">
                {(
                  [
                    "SERVICE_COMPLETED",
                    "SETTLEMENT_REQUESTED",
                    "SETTLEMENT_CONFIRMED",
                    "PAY_COMPLETED",
                  ] as SettlementStatusTypes[]
                ).map((v) => (
                  <Select.Option key={v} value={v} label={v}>
                    {translateSettlementStatusTypes[v]}
                  </Select.Option>
                ))}
              </CustomSelect>
            </Form.Item>
          </Col>
        </Row>
      </CustomForm>
      <InfoContainer>
        <div>
          <IconForm component={Question} style={{ fontSize: 16 }} />
          <p className="first">
            매월 1일부터 말일까지 장착완료 건에 대해서만 정산 가능합니다.
            <br />
            금액 확인 후 익월 5일까지 세금계산서 발행을 꼭 해주시기 바랍니다.
          </p>
        </div>
        <div>
          <IconForm
            component={Caution}
            color={color.primary3}
            style={{ fontSize: 16 }}
          />
          <p className="second">
            리스트에서 주문 건이 보이지 않는 경우 주문현황 메뉴에서 장착완료가
            되었는지 확인해주세요.
          </p>
        </div>
      </InfoContainer>
    </Card>
  );
});

const FilterLabel = styled.span`
  text-align: left;

  min-width: 65px;
  color: ${theme.colors.gray1};

  ${theme.typo.heading.b14};
`;

const InfoContainer = styled.div`
  margin-top: 24px;

  > div {
    display: flex;
    align-items: flex-start;
    :first-child {
      margin-bottom: 4px;
    }
    > p {
      margin-left: 4px;
      margin-bottom: 0;
      ${theme.typo.body.r12};
      color: ${theme.colors.gray1};
    }

    > .second {
      color: ${theme.colors.mainPrimary3};
    }
  }
`;
